import { Button, FormControl, FormLabel, Text, useToast } from '@chakra-ui/react';
import { PrePlacementDocumentType } from '@duke-hq/svc-api-db';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { Controller, useForm } from 'react-hook-form';
import { Select, SingleValue } from 'chakra-react-select';
import { FormModalWrapper } from '@duke-hq/ui/components/Modals/FormModalWrapper';
import {
  FormContentContainer,
  FormFooterContainer,
  FormMainContainer,
} from '@duke-hq/ui/components/Modals/FormModalLayouts';
import { getDocumentTypeLabel } from './documentTypeLabels';
import { testIds } from './testIds';

interface FormData {
  documentType: PrePlacementDocumentType | null;
}

interface AddRequirementModalProps {
  isOpen: boolean;
  onClose: () => void;
  journeyId: string;
  existingTypes: PrePlacementDocumentType[];
  onSuccess: () => void;
}

export function AddRequirementModal({
  isOpen,
  onClose,
  journeyId,
  existingTypes,
  onSuccess,
}: AddRequirementModalProps): React.JSX.Element {
  const toast = useToast();

  const { control, handleSubmit, reset } = useForm<FormData>({
    defaultValues: { documentType: null },
  });

  const addMutation = trpcAdmin.workPlacements.placements.addPlacementRequirement.useMutation({
    onSuccess: () => {
      toast({ status: 'success', title: t`Requirement added` });
      reset();
      onSuccess();
      onClose();
    },
    onError: (error) => {
      toast({ status: 'error', title: t`Failed to add requirement`, description: error.message });
    },
  });

  const handleClose = () => {
    reset();
    onClose();
  };

  const labels = getDocumentTypeLabel();
  const availableOptions = Object.values(PrePlacementDocumentType)
    .filter((type) => !existingTypes.includes(type))
    .map((type) => ({ label: labels[type], value: type }));

  const onSubmit = ({ documentType }: FormData) => {
    if (documentType == null) {
      return;
    }

    addMutation.mutate({ journeyId, documentType });
  };

  return (
    <FormModalWrapper
      isOpen={isOpen}
      onClose={handleClose}
      size="lg"
      modalContentHeight="auto"
      modalContent={
        <FormMainContainer>
          <FormContentContainer>
            <Text fontSize="lg" fontWeight="semibold" w="full">
              <Trans>Add new requirement</Trans>
            </Text>

            <FormControl isRequired>
              <FormLabel>
                <Trans>Requirement name</Trans>
              </FormLabel>
              <Controller
                name="documentType"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <Select
                    data-testid={testIds.documentTypeSelect}
                    options={availableOptions}
                    value={availableOptions.find((option) => option.value === field.value) ?? null}
                    onChange={(option: SingleValue<{ value: string | undefined; label: string }>) =>
                      field.onChange(option?.value ?? null)
                    }
                    placeholder={t`Select a requirement`}
                  />
                )}
              />
            </FormControl>
          </FormContentContainer>

          <FormFooterContainer>
            <Button isLoading={addMutation.isPending} onClick={handleSubmit(onSubmit)}>
              <Trans>Add</Trans>
            </Button>
            <Button variant="ghost" onClick={handleClose}>
              <Trans>Cancel</Trans>
            </Button>
          </FormFooterContainer>
        </FormMainContainer>
      }
    />
  );
}
